import { Dispatch, SetStateAction, useEffect } from "react";
import styled from "styled-components";
import { useAppSelector } from "../../../app/hooks";
import { profileState } from "../../../app/reducers/profile-slice";
import theme from "../../../style/theme";
import { CompanyDto } from "../../../types/company/company-dto";
import { DeliveryPayloadValidator } from "../../../utils/validator/delivery";
import { BasicCalendar } from "../../calendar/BasicCalendar";
import { BasicSingleSelector } from "../../selector/BasicSingleSelector";
import Asterisk from "../../asterisk/asterisk";

interface Props {
  clientsMap: { [key: string]: string };
  selectedClientId: string;
  agenciesMap: { [key: string]: string };
  selectedAgencyId: string;
  dateToWork: Date;
  deliveryPayloadValidator: DeliveryPayloadValidator;
  setClientsMap: Dispatch<SetStateAction<{ [key: string]: string }>>;
  setAgenciesMap: Dispatch<SetStateAction<{ [key: string]: string }>>;
  setSelectedClientId: Dispatch<SetStateAction<string>>;
  setSelectedAgencyId: Dispatch<SetStateAction<string>>;
  setDateToWork: Dispatch<SetStateAction<Date>>;
}

export const BasicInfoCard: React.FC<Props> = ({
  clientsMap,
  selectedClientId,
  agenciesMap,
  selectedAgencyId,
  dateToWork,
  deliveryPayloadValidator,
  setClientsMap,
  setAgenciesMap,
  setSelectedClientId,
  setSelectedAgencyId,
  setDateToWork,
}) => {
  const { profile } = useAppSelector(profileState);

  const initClientsMap = async () => {
    if (!profile) {
      return;
    }

    const clientsMapTemp: { [key: string]: string } = {};
    clientsMapTemp[profile.company.id] = profile.company.name;
    profile.clients.forEach((company: CompanyDto) => {
      clientsMapTemp[company.id] = company.name;
    });

    setClientsMap(clientsMapTemp);
  };

  const initAgenciesMap = async () => {
    if (!profile) {
      return;
    }

    const agenciesMapTemp: { [key: string]: string } = {};
    agenciesMapTemp[profile.company.id] = profile.company.name;
    profile.agencies.forEach((company: CompanyDto) => {
      agenciesMapTemp[company.id] = company.name;
    });

    setAgenciesMap(agenciesMapTemp);
  };

  const handleClickClientItem = (key: string) => {
    setSelectedClientId(key);
  };

  const handleClickAgencyItem = (key: string) => {
    setSelectedAgencyId(key);
  };

  useEffect(() => {
    initClientsMap();
    initAgenciesMap();
  }, []);

  return (
    <BasicInfoCardContainer>
      <BasicInfoCardTitle>기본 정보</BasicInfoCardTitle>
      <FlexContainer>
        <FieldContainer>고객사<Asterisk/></FieldContainer>
        <SelectorContainer>
          <BasicSingleSelector
            value={clientsMap[selectedClientId]}
            placeholder={"등록된 고객사 선택"}
            candidateMap={clientsMap}
            onClickSelectItem={handleClickClientItem}
            width="22rem"
            isError={deliveryPayloadValidator.clientId}
          />
        </SelectorContainer>
      </FlexContainer>
      <FlexContainer>
        <FieldContainer>대행사<Asterisk/></FieldContainer>
        <SelectorContainer>
          <BasicSingleSelector
            value={agenciesMap[selectedAgencyId]}
            placeholder={"등록된 대행사 선택"}
            candidateMap={agenciesMap}
            onClickSelectItem={handleClickAgencyItem}
            width="22rem"
            isError={deliveryPayloadValidator.agencyId}
          />
        </SelectorContainer>
      </FlexContainer>
      <FlexContainer>
        <FieldContainer>배송 날짜<Asterisk/></FieldContainer>
        <CalendarContainer>
          <BasicCalendar
            selectedDate={dateToWork}
            setSelectedDate={setDateToWork}
          />
        </CalendarContainer>
      </FlexContainer>
    </BasicInfoCardContainer>
  );
};

const BasicInfoCardContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const BasicInfoCardTitle = styled.div`
  font-weight: 600;
  font-size: 1.125rem;
  line-height: 1.375rem;
  color: ${theme.palette.primitives.black};
  margin-bottom: 1.25rem;
`;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1rem;
`;

const FieldContainer = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 0.813rem;
  line-height: 1rem;
  color: ${theme.palette.primitives.gray600};
`;

const SelectorContainer = styled.div`
  margin-left: 3.375rem;
`;

const CalendarContainer = styled.div`
  margin-left: 2.438rem;
`;
